import { createBrowserRouter, Navigate, Route, RouterProvider, Routes } from 'react-router-dom';
import { ComponentType, lazy } from 'react';
import PrivateLayout from '@pages/layouts/PrivateLayout';
import NoMatch from '@pages/Error/NoMatch';
import ErrorBoundary from '@pages/Error/Boundary';
import ServerError from '@pages/Error/ServerError';
import HomeLayout from '@pages/layouts/HomeLayaout';
import './styles/index.scss';
import './styles/index.css';
import 'react-tooltip/dist/react-tooltip.css';
import SecurityLayout from '@pages/layouts/SecurityLayout';
import Login from '@pages/Security/Login';
import { Providers } from '@app/Providers';
import AccountShow from '@pages/Account/Show';
import AccountsDynamics from '@pages/AccountsDynamics/List';
import ManagersPerformancePage from '@pages/managers-performance';
import LeftHashRateShow from '@pages/Main/components/LeftHashRate';
import IncomeHashRateShow from '@pages/Main/components/IncomeHashRate';
import IncreaseHashRateShow from '@pages/Main/components/IncreaseHashRate';
import HostingList from '@pages/Hosting/List';
import HostingIndicators from '@pages/Hosting/Main';
import HostingShow from '@pages/Hosting/Show';

export const lazyMinLoadTime = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  minLoadTimeMs = 2000
) =>
  lazy(() =>
    Promise.all([factory(), new Promise(resolve => setTimeout(resolve, minLoadTimeMs))]).then(
      ([moduleExports]) => moduleExports
    )
  );

const MainIndicators = lazyMinLoadTime(() => import('pages/Main'), 1);

const CustomerList = lazyMinLoadTime(() => import('@pages/Customer/List'), 1);
const CustomerShow = lazyMinLoadTime(() => import('@pages/Customer/Show'), 1);

const UserList = lazyMinLoadTime(() => import('@pages/User/List'), 1);
const UserShow = lazyMinLoadTime(() => import('@pages/User/Show'), 1);

const AccountList = lazyMinLoadTime(() => import('@pages/Account/List'), 1);

const LostList = lazyMinLoadTime(() => import('@pages/Lost/List'), 1);

const ApplicationBoard = lazyMinLoadTime(() => import('@pages/Application/Board'), 1);
const ApplicationUpload = lazyMinLoadTime(() => import('@pages/Application/Upload'), 1);

const router = createBrowserRouter([
  {
    element: <SecurityLayout />,
    children: [
      {
        path: '/login',
        element: <Login />,
      },
    ],
  },
  {
    element: <PrivateLayout />,
    children: [
      {
        path: '',
        element: <Navigate replace to="/via-btc/main" />,
      },
      {
        path: '/via-btc/',
        element: <HomeLayout />,
        children: [
          {
            path: '/via-btc/main',
            element: <MainIndicators />,
          },
          {
            path: '/via-btc/main/left-hashrate',
            element: <LeftHashRateShow />,
          },
          {
            path: '/via-btc/main/income-hashrate',
            element: <IncomeHashRateShow />,
          },
          {
            path: '/via-btc/main/increase-hashrate',
            element: <IncreaseHashRateShow />,
          },
          {
            path: '/via-btc/account',
            element: <AccountList />,
          },
          {
            path: '/via-btc/account/:id',
            element: <AccountShow />,
          },
          {
            path: '/via-btc/account',
            element: <AccountList />,
          },
          {
            path: '/via-btc/account/:id',
            element: <AccountShow />,
          },
          {
            path: '/via-btc/customer',
            element: <CustomerList />,
          },
          {
            path: '/via-btc/customer/:id',
            element: <CustomerShow />,
          },
          {
            path: '/via-btc/board',
            element: <ApplicationBoard />,
          },
          {
            path: '/via-btc/lost',
            element: <LostList />,
          },
          {
            path: '/via-btc/dynamics',
            element: <AccountsDynamics />,
          },

          {
            path: '/via-btc/managers-performance',
            element: <ManagersPerformancePage />,
          },
          {
            path: '/via-btc/upload',
            element: <ApplicationUpload />,
          },
        ],
      },
      {
        path: '/hosting/',
        element: <HomeLayout />,
        children: [
          {
            path: '/hosting/main',
            element: <HostingIndicators />,
          },
          {
            path: '/hosting/list',
            element: <HostingList />,
          },
          {
            path: '/hosting/:id',
            element: <HostingShow />,
          },
        ],
      },
      {
        path: '/finance',
        element: <HomeLayout />,
        children: [
          {
            index: true,
            element: <MainIndicators />,
          },
        ],
      },
      {
        path: '/user',
        element: <HomeLayout />,
        children: [
          {
            index: true,
            element: <UserList />,
          },
          {
            path: '/user/:id',
            element: <UserShow />,
          },
        ],
      },
      {
        path: '/finance',
        element: <HomeLayout />,
        children: [
          {
            index: true,
            element: <MainIndicators />,
          },
        ],
      },
      {
        path: '/report',
        element: <HomeLayout />,
        children: [
          {
            index: true,
            element: <MainIndicators />,
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <NoMatch />,
  },
  {
    path: '/404',
    element: <NoMatch />,
  },
  {
    path: '/500',
    element: <ServerError />,
  },
]);

function App() {
  return (
    <ErrorBoundary action={() => (window.location.href = '/home')}>
      <Providers>
        <RouterProvider router={router} />
      </Providers>
    </ErrorBoundary>
  );
}

export default App;
